import React, { useState, useEffect, useCallback, memo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  IconButton,
  Grid,
  Alert,
  FormControlLabel,
  Switch,
  Chip,
} from '@mui/material';
import { Close, Business, CheckCircle, Warning, Info } from '@mui/icons-material';
import { THEME_COLORS } from '../../constants';
import { useDistributorValidation } from '../../hooks/useDistributorValidation';
import { formatPhone, formatBankAccount } from '../../utils/distributorValidation';

const initialFormData = {
  nombre_empresa: '',
  nombre_distribuidor: '',
  celular: '',
  cuenta_ewelink: '',
  numero_cuenta_bancaria: '',
  ciudad: '',
  estado: '',
  is_active: true,
};

const DistributorFormModalComponent = ({ open, onClose, onSubmit, entity, isEditing }) => {
  const [formData, setFormData] = useState(initialFormData);
  const [submitError, setSubmitError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Hook de validación
  const {
    errors,
    isFormValid,
    validateSingleField,
    validateForm,
    getFieldError,
    hasFieldError,
    getSuggestions,
    clearAllErrors
  } = useDistributorValidation();

  // Función para limpiar el número de celular para mostrar en el formulario
  const cleanPhoneForDisplay = (phone) => {
    if (!phone) return '';

    // Limpiar espacios y caracteres especiales
    const cleanPhone = phone.replace(/[\s\-]/g, '');

    // Si es un número mexicano (+52XXXXXXXXXX), mostrar solo los 10 dígitos
    if (cleanPhone.startsWith('+52') && cleanPhone.length === 13) {
      return cleanPhone.slice(3); // Remover +52
    }

    // Si es un número mexicano sin + (52XXXXXXXXXX), mostrar solo los 10 dígitos
    if (cleanPhone.startsWith('52') && cleanPhone.length === 12 && /^\d+$/.test(cleanPhone)) {
      return cleanPhone.slice(2); // Remover 52
    }

    // Si ya son 10 dígitos exactos, mantenerlo
    if (/^\d{10}$/.test(cleanPhone)) {
      return cleanPhone;
    }

    // Para otros prefijos internacionales, mostrar el número completo
    return phone;
  };

  useEffect(() => {
    if (open) {
      if (isEditing && entity) {
        setFormData({
          nombre_empresa: entity.nombre_empresa || '',
          nombre_distribuidor: entity.nombre_distribuidor || '',
          celular: cleanPhoneForDisplay(entity.celular || ''),
          cuenta_ewelink: entity.cuenta_ewelink || '',
          numero_cuenta_bancaria: entity.numero_cuenta_bancaria || '',
          ciudad: entity.ciudad || '',
          estado: entity.estado || '',
          is_active: entity.is_active !== undefined ? entity.is_active : true,
        });
      } else {
        setFormData(initialFormData);
      }
      clearAllErrors();
      setSubmitError('');
      setIsSubmitting(false);
    }
  }, [entity, isEditing, open, clearAllErrors]);

  const handleInputChange = useCallback((field) => (event) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setFormData(prev => ({ ...prev, [field]: value }));

    // Validar el campo en tiempo real si ya ha sido tocado
    if (field !== 'is_active') {
      validateSingleField(field, value, false);
    }
  }, [validateSingleField]);

  const handleFieldBlur = useCallback((field) => () => {
    if (field !== 'is_active') {
      validateSingleField(field, formData[field], true);
    }
  }, [validateSingleField, formData]);

  // Obtener sugerencias de validación
  const suggestions = getSuggestions(formData);

  const handleSubmit = useCallback(async (event) => {
    event.preventDefault();
    setIsSubmitting(true);
    setSubmitError('');

    // Validar todo el formulario
    const validation = validateForm(formData);

    if (!validation.isValid) {
      setIsSubmitting(false);
      return;
    }

    try {
      // Usar los datos limpios de la validación
      await onSubmit(validation.cleanData);
    } catch (error) {
      setSubmitError(error.message || 'Ocurrió un error al guardar el distribuidor.');
      setIsSubmitting(false);
    }
  }, [formData, onSubmit, validateForm]);

  const handleClose = () => {
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '16px',
          border: `1px solid ${THEME_COLORS.BORDER_LIGHT}`,
          backgroundColor: THEME_COLORS.BACKGROUND_PAPER,
          boxShadow: `0 8px 32px ${THEME_COLORS.SHADOW_LIGHT}`,
          overflow: 'hidden',
        }
      }}
    >
      <DialogTitle
        sx={{
          padding: 4,
          paddingBottom: 3,
          backgroundColor: THEME_COLORS.BACKGROUND_DEFAULT,
          borderBottom: `1px solid ${THEME_COLORS.BORDER_LIGHT}`,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: '12px',
                backgroundColor: THEME_COLORS.TEXT_PRIMARY,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Business sx={{ color: THEME_COLORS.WHITE, fontSize: '1.5rem' }} />
            </Box>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 600, color: THEME_COLORS.TEXT_PRIMARY, fontSize: '1.25rem' }}>
                {isEditing ? 'Editar Distribuidor' : 'Nuevo Distribuidor'}
              </Typography>
              <Typography variant="body2" sx={{ color: THEME_COLORS.TEXT_SECONDARY, fontSize: '0.875rem' }}>
                {isEditing ? 'Modifica los datos del distribuidor.' : 'Agrega un nuevo distribuidor al sistema.'}
              </Typography>
            </Box>
          </Box>
          <IconButton onClick={handleClose} sx={{ color: THEME_COLORS.TEXT_SECONDARY, '&:hover': { backgroundColor: THEME_COLORS.BACKGROUND_PAPER, color: THEME_COLORS.TEXT_PRIMARY } }}>
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ padding: 4, paddingTop: 3 }}>
        {submitError && (
          <Alert severity="error" sx={{ mb: 2, borderRadius: '12px' }}>
            {submitError}
          </Alert>
        )}
        <Grid container spacing={3}>
          {/* Información Principal */}
          <Grid item xs={12}>
            <Typography variant="subtitle1" sx={{ color: THEME_COLORS.TEXT_PRIMARY, fontWeight: 600, fontSize: '1rem', marginBottom: 2 }}>
              Información Principal
            </Typography>
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              label="Nombre de la Empresa"
              placeholder="Ej: Distribuidora ABC S.A."
              value={formData.nombre_empresa}
              onChange={handleInputChange('nombre_empresa')}
              onBlur={handleFieldBlur('nombre_empresa')}
              error={hasFieldError('nombre_empresa')}
              helperText={getFieldError('nombre_empresa') || 'Nombre comercial de la empresa distribuidora.'}
              fullWidth
              required
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' }, '& .MuiInputLabel-root': { color: THEME_COLORS.TEXT_SECONDARY } }}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              label="Nombre del Distribuidor"
              placeholder="Ej: Juan Pérez"
              value={formData.nombre_distribuidor}
              onChange={handleInputChange('nombre_distribuidor')}
              onBlur={handleFieldBlur('nombre_distribuidor')}
              error={hasFieldError('nombre_distribuidor')}
              helperText={getFieldError('nombre_distribuidor') || 'Nombre de la persona responsable.'}
              fullWidth
              required
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' }, '& .MuiInputLabel-root': { color: THEME_COLORS.TEXT_SECONDARY } }}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              label="Número de Celular"
              placeholder="Ej: 2299288981 o +1 1234567890"
              value={formData.celular}
              onChange={handleInputChange('celular')}
              onBlur={handleFieldBlur('celular')}
              error={hasFieldError('celular')}
              helperText={getFieldError('celular') || 'Para México: solo 10 dígitos (ej: 2299288981). Para otros países: incluye prefijo (ej: +1 1234567890).'}
              fullWidth
              required
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' }, '& .MuiInputLabel-root': { color: THEME_COLORS.TEXT_SECONDARY } }}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              label="Cuenta eWeLink"
              placeholder="Ej: distribuidor@empresa.com"
              value={formData.cuenta_ewelink}
              onChange={handleInputChange('cuenta_ewelink')}
              onBlur={handleFieldBlur('cuenta_ewelink')}
              error={hasFieldError('cuenta_ewelink')}
              helperText={getFieldError('cuenta_ewelink') || 'Email asociado a la cuenta de eWeLink.'}
              fullWidth
              required
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' }, '& .MuiInputLabel-root': { color: THEME_COLORS.TEXT_SECONDARY } }}
            />
          </Grid>

          {/* Información Adicional */}
          <Grid item xs={12}>
            <Typography variant="subtitle1" sx={{ color: THEME_COLORS.TEXT_PRIMARY, fontWeight: 600, fontSize: '1rem', marginBottom: 2, mt: 2 }}>
              Información Adicional (Opcional)
            </Typography>
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              label="Número de Cuenta Bancaria"
              placeholder="Ej: 1234567890"
              value={formData.numero_cuenta_bancaria}
              onChange={handleInputChange('numero_cuenta_bancaria')}
              onBlur={handleFieldBlur('numero_cuenta_bancaria')}
              error={hasFieldError('numero_cuenta_bancaria')}
              helperText={getFieldError('numero_cuenta_bancaria') || 'Cuenta para pagos y transferencias.'}
              fullWidth
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' }, '& .MuiInputLabel-root': { color: THEME_COLORS.TEXT_SECONDARY } }}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              label="Ciudad"
              placeholder="Ej: Puebla"
              value={formData.ciudad}
              onChange={handleInputChange('ciudad')}
              onBlur={handleFieldBlur('ciudad')}
              error={hasFieldError('ciudad')}
              helperText={getFieldError('ciudad') || 'Ciudad donde opera el distribuidor.'}
              fullWidth
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' }, '& .MuiInputLabel-root': { color: THEME_COLORS.TEXT_SECONDARY } }}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              label="Estado"
              placeholder="Ej: Puebla"
              value={formData.estado}
              onChange={handleInputChange('estado')}
              onBlur={handleFieldBlur('estado')}
              error={hasFieldError('estado')}
              helperText={getFieldError('estado') || 'Estado de operación.'}
              fullWidth
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' }, '& .MuiInputLabel-root': { color: THEME_COLORS.TEXT_SECONDARY } }}
            />
          </Grid>

          {/* Estado Activo */}
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', pt: 2 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.is_active}
                    onChange={handleInputChange('is_active')}
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': {
                        color: THEME_COLORS.TEXT_PRIMARY,
                      },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                        backgroundColor: THEME_COLORS.TEXT_PRIMARY,
                      },
                    }}
                  />
                }
                label={
                  <Box>
                    <Typography variant="body1" sx={{ color: THEME_COLORS.TEXT_PRIMARY, fontWeight: 500 }}>
                      Distribuidor Activo
                    </Typography>
                    <Typography variant="body2" sx={{ color: THEME_COLORS.TEXT_SECONDARY, fontSize: '0.75rem' }}>
                      {formData.is_active ? 'El distribuidor puede recibir contactos asignados' : 'El distribuidor está desactivado'}
                    </Typography>
                  </Box>
                }
                sx={{ margin: 0 }}
              />
            </Box>
          </Grid>
        </Grid>

        {/* Sugerencias de validación */}
        {suggestions.length > 0 && (
          <Box sx={{ mt: 3 }}>
            <Typography variant="subtitle2" sx={{ color: THEME_COLORS.TEXT_PRIMARY, fontWeight: 600, mb: 1 }}>
              Sugerencias
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {suggestions.map((suggestion, index) => (
                <Chip
                  key={index}
                  icon={
                    suggestion.type === 'warning' ? <Warning sx={{ fontSize: '1rem' }} /> :
                      suggestion.type === 'info' ? <Info sx={{ fontSize: '1rem' }} /> :
                        <CheckCircle sx={{ fontSize: '1rem' }} />
                  }
                  label={suggestion.message}
                  size="small"
                  variant="outlined"
                  sx={{
                    borderRadius: '8px',
                    backgroundColor:
                      suggestion.type === 'warning' ? 'rgba(255, 152, 0, 0.1)' :
                        suggestion.type === 'info' ? 'rgba(33, 150, 243, 0.1)' :
                          'rgba(76, 175, 80, 0.1)',
                    borderColor:
                      suggestion.type === 'warning' ? 'rgba(255, 152, 0, 0.3)' :
                        suggestion.type === 'info' ? 'rgba(33, 150, 243, 0.3)' :
                          'rgba(76, 175, 80, 0.3)',
                    color: THEME_COLORS.TEXT_SECONDARY,
                    '& .MuiChip-icon': {
                      color:
                        suggestion.type === 'warning' ? THEME_COLORS.TEXT_MUTED :
                          suggestion.type === 'info' ? THEME_COLORS.SECONDARY :
                            THEME_COLORS.TEXT_SECONDARY
                    }
                  }}
                />
              ))}
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          padding: 4,
          paddingTop: 2,
          backgroundColor: THEME_COLORS.BACKGROUND_DEFAULT,
          borderTop: `1px solid ${THEME_COLORS.BORDER_LIGHT}`,
          gap: 2,
        }}
      >
        <Button
          onClick={handleClose}
          variant="outlined"
          sx={{
            borderRadius: '12px',
            paddingX: 3,
            paddingY: 1.5,
            textTransform: 'none',
            fontWeight: 600,
            borderColor: THEME_COLORS.BORDER,
            color: THEME_COLORS.TEXT_SECONDARY,
            '&:hover': {
              borderColor: THEME_COLORS.TEXT_PRIMARY,
              color: THEME_COLORS.TEXT_PRIMARY,
              backgroundColor: THEME_COLORS.BACKGROUND_PAPER,
            },
          }}
        >
          Cancelar
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={isSubmitting}
          sx={{
            backgroundColor: THEME_COLORS.TEXT_PRIMARY,
            color: THEME_COLORS.WHITE,
            borderRadius: '12px',
            paddingX: 3,
            paddingY: 1.5,
            textTransform: 'none',
            fontWeight: 600,
            '&:hover': { backgroundColor: THEME_COLORS.TEXT_PRIMARY },
            '&:disabled': {
              backgroundColor: THEME_COLORS.BORDER_LIGHT,
              color: THEME_COLORS.TEXT_SECONDARY
            }
          }}
        >
          {isSubmitting ? 'Guardando...' : (isEditing ? 'Actualizar Distribuidor' : 'Crear Distribuidor')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const DistributorFormModal = memo(DistributorFormModalComponent);
DistributorFormModal.displayName = 'DistributorFormModal';

export default DistributorFormModal;